/**
 * #2945 — the team's ward rows in cover groups, pure. No React, no I/O.
 *
 * The department's week sheet (the KKH *Weekly Combined Deployment* xlsx)
 * puts each ward under a cover-group letter in its column A. This module
 * reads the same facts from the team config (`GET /api/teams/{id}/config`,
 * open to every active member) and follows `ward_rows` in
 * `backend/src/api/routes/insights/week_sheet.py`, so the phone, the laptop
 * and the xlsx agree on the rows and their order:
 *
 *   - A ward row is an enabled station that is not an on-call post, not
 *     marked non-ward and not named like a clinic. Rows keep config order.
 *   - Cover groups stay together, in order of first appearance. Stations
 *     with no group come last.
 *   - A team with no cover group on any ward is flat: one list, no head rows.
 *
 * Station groups in Setup (solve stages, lead-only) are a different thing
 * and are not read here.
 *
 * Consumers: the week sheet model (`weekSheetModel.ts`, slice 1), the
 * laptop Stations rows (slice 2), the laptop Sheet rows (slice 3), Team
 * today (slice 4) and "With you" in the Calendar (slice 5). Exported API:
 *
 *   - `isClinicStation(name)`         a station named like a clinic.
 *   - `isWardStation(station)`        the ward-row rule above.
 *   - `coverGroupOf(stations, ward)`  the ward's group letter, or null.
 *   - `wardGroups(stations, extra?)`  the ward rows in groups (+ `flat`).
 *   - `groupTitle(group)`             "Group A" for a one-letter group, the
 *                                     text itself for a longer one ("Medical
 *                                     wards"), "Stations" for null.
 *   - `groupHeadLine(group)`          "Group A · 31 · 55 · 51".
 *   - `groupWards(stations, letter)`  the wards of one group, in order.
 *   - `contactLinks(stations, ward)`  the wards ticked under "Contact from
 *                                     other wards", known wards only.
 *   - `otherSide(stations, ward)`     the other side of a ward: its contact
 *                                     links first, then the other wards of
 *                                     its cover group (founder Q2 (a)).
 */

/** The station facts this module reads (a subset of `StationEntry`). */
export interface SheetStation {
  name: string;
  cover_group?: string | null;
  contact_from?: readonly string[] | null;
  on_call?: boolean | null;
  non_ward?: boolean | null;
  /** Missing means enabled, as on the API (`enabled: bool = True`). */
  enabled?: boolean | null;
}

/** One cover group of ward rows. */
export interface WardGroup {
  /** Stable key: `cover:<letter>` or `ungrouped`. */
  key: string;
  /** The cover group as the config gives it (often a letter); `null` for
   *  the stations with no group. */
  letter: string | null;
  /** "Group A", the group's own text, or "Stations" for the ungrouped tail. */
  title: string;
  /** Ward names in sheet order. */
  wards: string[];
}

export interface WardGroups {
  /** True when no ward carries a cover group: one list, no head rows. */
  flat: boolean;
  groups: WardGroup[];
}

const CLINIC_RE = /clinic/i;

/** Ungrouped title, as Team home names its plain stations (#2873). */
export const UNGROUPED_TITLE = "Stations";

function letterOf(s: SheetStation): string | null {
  const g = s.cover_group?.trim();
  return g ? g : null;
}

/** A station named like a clinic: the sheet's CLINIC row, never a ward row. */
export function isClinicStation(name: string): boolean {
  return CLINIC_RE.test(name);
}

/** The ward-row rule of `week_sheet.ward_rows`. */
export function isWardStation(s: SheetStation): boolean {
  return (
    s.enabled !== false && !s.on_call && !s.non_ward && Boolean(s.name) && !isClinicStation(s.name)
  );
}

/** The ward's cover group (case-insensitive name match), or null. */
export function coverGroupOf(stations: readonly SheetStation[], ward: string): string | null {
  const fold = ward.toLocaleLowerCase();
  const hit = stations.find((s) => s.name.toLocaleLowerCase() === fold);
  return hit ? letterOf(hit) : null;
}

/**
 * The head-row title of a cover group. `cover_group` is free text (up to
 * 40 characters): a single letter reads "Group A", anything longer reads as
 * the lead wrote it, and no group reads "Stations".
 */
export function groupTitle(group: string | null): string {
  if (!group) return UNGROUPED_TITLE;
  return /^\p{L}$/u.test(group) ? `Group ${group}` : group;
}

/** The head row of a group: "Group A · 31 · 55 · 51". */
export function groupHeadLine(group: Pick<WardGroup, "title" | "wards">): string {
  return [group.title, ...group.wards].join(" · ");
}

/**
 * The ward rows in groups, in sheet order. `extraNames` are stations an
 * assignment names that the config no longer carries (the xlsx appends them
 * to the ungrouped tail); names the config knows are never added twice, and
 * a config station that is not a ward (call post, non-ward, clinic,
 * disabled) never becomes a row through this path.
 */
export function wardGroups(
  stations: readonly SheetStation[],
  extraNames: readonly string[] = [],
): WardGroups {
  const wards = stations.filter(isWardStation);
  const order: string[] = [];
  const byKey = new Map<string, WardGroup>();
  for (const s of wards) {
    const letter = letterOf(s);
    const key = letter ? `cover:${letter}` : "ungrouped";
    let group = byKey.get(key);
    if (!group) {
      group = { key, letter, title: groupTitle(letter), wards: [] };
      byKey.set(key, group);
      order.push(key);
    }
    group.wards.push(s.name);
  }
  const known = new Set(stations.map((s) => s.name.toLocaleLowerCase()));
  for (const name of extraNames) {
    const fold = name.toLocaleLowerCase();
    if (!name || known.has(fold) || isClinicStation(name)) continue;
    known.add(fold);
    let tail = byKey.get("ungrouped");
    if (!tail) {
      tail = { key: "ungrouped", letter: null, title: groupTitle(null), wards: [] };
      byKey.set("ungrouped", tail);
      order.push("ungrouped");
    }
    tail.wards.push(name);
  }
  const groups = order
    .map((k) => byKey.get(k) as WardGroup)
    .sort((a, b) => Number(a.letter === null) - Number(b.letter === null));
  const flat = !groups.some((g) => g.letter !== null);
  return { flat, groups };
}

/** The wards of one cover group, in sheet order. */
export function groupWards(stations: readonly SheetStation[], letter: string): string[] {
  return stations.filter((s) => isWardStation(s) && letterOf(s) === letter).map((s) => s.name);
}

/** The wards a ward ticks under "Contact from other wards" that are still wards. */
export function contactLinks(stations: readonly SheetStation[], ward: string): string[] {
  const fold = ward.toLocaleLowerCase();
  const self = stations.find((s) => s.name.toLocaleLowerCase() === fold);
  if (!self) return [];
  const wardNames = new Map(
    stations.filter(isWardStation).map((s) => [s.name.toLocaleLowerCase(), s.name]),
  );
  const out: string[] = [];
  for (const raw of self.contact_from ?? []) {
    const name = wardNames.get(raw.toLocaleLowerCase());
    if (name && name.toLocaleLowerCase() !== fold && !out.includes(name)) out.push(name);
  }
  return out;
}

/**
 * The other side of a ward (founder Q2 (a)): the wards it ticks under
 * "Contact from other wards" first, then the other wards of its cover group
 * in sheet order. Never the ward itself. Empty for an ungrouped ward with no
 * contact links.
 */
export function otherSide(stations: readonly SheetStation[], ward: string): string[] {
  const fold = ward.toLocaleLowerCase();
  const out = contactLinks(stations, ward);
  const letter = coverGroupOf(stations, ward);
  if (letter) {
    for (const name of groupWards(stations, letter)) {
      if (name.toLocaleLowerCase() !== fold && !out.includes(name)) out.push(name);
    }
  }
  return out;
}
